import { useEffect, useState, useRef } from "react";
import api, { fmtMXN, fmtDate, todayLocal } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { In, Sel } from "./Projects";
import ReceiptViewer from "@/components/ReceiptViewer";
import { Plus, Trash2, Pencil, XCircle, Eye, Sparkles, Banknote, HandCoins, ClipboardCheck, ChevronDown, ChevronUp, Landmark } from "lucide-react";
import TreasuryTab from "@/components/TreasuryTab";
import { toast } from "sonner";

const PAGE_SIZE = 50;
const STATUS_COLORS = { pendiente: "#fbbf24", aprobado: "#22d3ee", pagado: "#22c55e" };

const Kpi = ({ label, value, color, testId, big }) => (
  <div className="panel p-4" data-testid={testId}>
    <div className="label-mono" style={{ color }}>{label}</div>
    <div className={`font-mono font-bold mt-1 ${big ? "text-2xl" : "text-lg"}`} style={{ color }}>{fmtMXN(value)}</div>
  </div>
);

const Pager = ({ page, setPage, total, testId }) => total > PAGE_SIZE ? (
  <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: "1px solid var(--border)" }} data-testid={testId}>
    <button disabled={page === 0} onClick={() => setPage(page - 1)} className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40" style={{ border: "1px solid var(--border)" }}>← Anterior</button>
    <span className="text-xs font-mono text-slate-400">Página <b className="text-slate-100">{page + 1}</b> de {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
    <button disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(page + 1)} className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40" style={{ border: "1px solid var(--border)" }}>Siguiente →</button>
  </div>
) : null;

const emptyExchange = { exchange_date: todayLocal(), amount_transferred: "", commission: "", commission_pct: "3", transferred_to: "", performed_by: "", reference: "", notes: "" };
const PCT_OPTIONS = [3, 4, 5, 6, 7, 8, 9, 10].map((p) => ({ value: String(p), label: `${p} %` })).concat([{ value: "", label: "Manual ($)" }]);

const ExchangeModal = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState(editing
    ? { ...emptyExchange, ...editing, commission_pct: editing.commission_pct != null ? String(editing.commission_pct) : "" }
    : emptyExchange);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [voucherFile, setVoucherFile] = useState(null);
  const [staff, setStaff] = useState([]);
  useEffect(() => { api.get("/cash/staff").then(({ data }) => setStaff(data || [])).catch(() => {}); }, []);
  const scanRef = useRef(null);
  const amount = Number(form.amount_transferred) || 0;
  const pctMode = form.commission_pct !== "";
  const commission = pctMode
    ? Math.round(amount * Number(form.commission_pct)) / 100
    : Math.round((Number(form.commission) || 0) * 100) / 100;
  const received = Math.round((amount - commission) * 100) / 100;

  const scan = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!navigator.onLine) { toast.error("Necesitas internet para usar el escáner con IA."); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cash/scan-voucher", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setForm((f) => ({
        ...f,
        amount_transferred: data.amount_transferred ?? f.amount_transferred,
        commission: data.commission ?? f.commission,
        commission_pct: data.commission != null ? "" : f.commission_pct,
        transferred_to: data.transferred_to || f.transferred_to,
        exchange_date: data.exchange_date || f.exchange_date,
        reference: data.reference || f.reference,
      }));
      setVoucherFile(file);
      toast.success("Comprobante leído. Verifica los datos antes de guardar.");
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo escanear el comprobante"); }
    setScanning(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      let attachment_id = editing?.attachment_id || null;
      if (voucherFile) {
        const fd = new FormData();
        fd.append("file", voucherFile);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = up.data.id;
      }
      const payload = {
        ...form,
        amount_transferred: Math.round(Number(form.amount_transferred) * 100) / 100,
        commission,
        commission_pct: pctMode ? Number(form.commission_pct) : null,
        attachment_id,
      };
      if (editing) await api.patch(`/cash/exchanges/${editing.id}`, payload);
      else await api.post("/cash/exchanges", payload);
      toast.success(editing ? "Cambio actualizado" : "Cambio registrado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(3,6,14,0.8)", backdropFilter: "blur(4px)" }}>
      <div className="panel w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" data-testid="cash-exchange-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-slate-100">{editing ? "Editar cambio de efectivo" : "Registrar cambio de efectivo"}</h3>
          <button onClick={onClose} className="p-1 rounded-sm hover:bg-white/5 text-slate-400"><XCircle className="w-5 h-5" /></button>
        </div>
        <button type="button" data-testid="cash-scan-btn" disabled={scanning} onClick={() => scanRef.current?.click()}
          className="w-full mb-4 px-4 py-2.5 rounded-sm flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-60"
          style={{ border: "1px dashed var(--magenta, #d946ef)", color: "var(--magenta, #d946ef)", background: "#d946ef10" }}>
          <Sparkles className="w-4 h-4" /> {scanning ? "Leyendo comprobante…" : "Escanear comprobante con IA"}
        </button>
        <input ref={scanRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={scan} />
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <In label="Fecha" type="date" value={form.exchange_date} onChange={(v) => setForm({ ...form, exchange_date: v })} testId="cash-ex-date" />
            <In label="Monto transferido" type="number" value={form.amount_transferred} onChange={(v) => setForm({ ...form, amount_transferred: v })} testId="cash-ex-amount" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Sel label="% de comisión" value={form.commission_pct} onChange={(v) => setForm({ ...form, commission_pct: v })} options={PCT_OPTIONS} testId="cash-ex-pct" />
            {pctMode ? (
              <div>
                <label className="label-mono block mb-1.5">Comisión cobrada</label>
                <div className="input-cyber px-3 py-2 text-sm font-mono font-bold" style={{ color: "var(--amber, #d97706)" }} data-testid="cash-ex-commission-calc">−{fmtMXN(commission)}</div>
              </div>
            ) : (
              <In label="Comisión cobrada ($)" type="number" value={form.commission} onChange={(v) => setForm({ ...form, commission: v })} testId="cash-ex-commission" />
            )}
          </div>
          <div>
            <label className="label-mono block mb-1.5">Efectivo a recibir {pctMode && amount > 0 ? `(${fmtMXN(amount)} − ${form.commission_pct}%)` : ""}</label>
            <div className="input-cyber px-3 py-2 text-base font-mono font-bold" style={{ color: "var(--green)" }} data-testid="cash-ex-received">{fmtMXN(received)}</div>
          </div>
          <In label="A quién se transfirió" value={form.transferred_to} onChange={(v) => setForm({ ...form, transferred_to: v })} testId="cash-ex-to" />
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Quién realizó / recibió" value={form.performed_by || ""} onChange={(v) => setForm({ ...form, performed_by: v })}
              options={[{ value: "", label: "— Selecciona —" },
                ...staff.map((u) => ({ value: u.name, label: `${u.name} (${u.role})` })),
                ...(form.performed_by && !staff.some((u) => u.name === form.performed_by) ? [{ value: form.performed_by, label: form.performed_by }] : [])]}
              testId="cash-ex-by" />
            <In label="Referencia / folio" required={false} value={form.reference || ""} onChange={(v) => setForm({ ...form, reference: v })} testId="cash-ex-ref" />
          </div>
          <div>
            <label className="label-mono block mb-1.5">Notas</label>
            <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-mono block mb-1.5">Comprobante (opcional)</label>
            <input type="file" accept="image/*,application/pdf" data-testid="cash-ex-file"
              onChange={(e) => setVoucherFile(e.target.files?.[0] || null)}
              className="w-full text-xs text-slate-400 file:mr-3 file:px-3 file:py-1.5 file:rounded-sm file:border file:border-cyan-500/40 file:bg-transparent file:text-cyan-400 file:text-[10px] file:uppercase file:tracking-widest" />
            {voucherFile && <div className="text-[10px] text-slate-500 mt-1">{voucherFile.name}</div>}
            {!voucherFile && editing?.attachment_id && <div className="text-[10px] text-slate-500 mt-1">Ya tiene comprobante adjunto</div>}
          </div>
          <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
            <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
            <button type="submit" data-testid="cash-ex-save-btn" disabled={saving} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">{saving ? "Guardando…" : editing ? "Actualizar" : "Guardar"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ExchangesTab = ({ canDelete, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [sums, setSums] = useState(null);
  const [page, setPage] = useState(0);
  const [fStart, setFStart] = useState("");
  const [fEnd, setFEnd] = useState("");
  const [modal, setModal] = useState(null); // null | { editing }
  const [viewer, setViewer] = useState(null);

  const load = async () => {
    const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    if (fStart) params.start_date = fStart;
    if (fEnd) params.end_date = fEnd;
    const { data } = await api.get("/cash/exchanges", { params });
    setRows(data.items || []); setTotal(data.total || 0); setSums(data.sums || null);
  };
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [fStart, fEnd]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fStart, fEnd, page]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este cambio de efectivo?")) return;
    try { await api.delete(`/cash/exchanges/${id}`); load(); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div className="panel p-3 grid grid-cols-2 gap-2">
          <In label="Desde" type="date" required={false} value={fStart} onChange={setFStart} testId="cash-ex-filter-start" />
          <In label="Hasta" type="date" required={false} value={fEnd} onChange={setFEnd} testId="cash-ex-filter-end" />
        </div>
        <button data-testid="add-cash-exchange-btn" onClick={() => setModal({ editing: null })}
          className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
          <Plus className="w-4 h-4" /> Registrar cambio
        </button>
      </div>
      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1080px]" data-testid="cash-exchanges-table">
            <thead className="panel-header">
              <tr className="text-left">
                {["Acciones", "Fecha", "Transferido a", "Transferido", "Comisión", "Efectivo recibido", "Factura", "Realizó / Recibió", "Referencia"].map((h) => (
                  <th key={h} className={`label-mono px-4 py-3 ${["Transferido", "Comisión", "Efectivo recibido"].includes(h) ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-500">Sin cambios de efectivo registrados.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }} data-testid={`cash-ex-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-0.5">
                      <button data-testid={`cash-ex-edit-${r.id}`} onClick={() => setModal({ editing: r })} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }} title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                      {canDelete && <button data-testid={`cash-ex-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }} title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>}
                      {r.attachment_id && <button data-testid={`cash-ex-view-${r.id}`} onClick={() => setViewer(r.attachment_id)} className="p-1.5 rounded-sm hover:bg-white/5 text-slate-400" title="Ver comprobante"><Eye className="w-3.5 h-3.5" /></button>}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-300 whitespace-nowrap">{fmtDate(r.exchange_date)}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-slate-100">{r.transferred_to}</span>
                    {r.notes && <div className="text-[10px] text-slate-500 truncate max-w-[220px]" title={r.notes}>{r.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300 whitespace-nowrap">{fmtMXN(r.amount_transferred)}</td>
                  <td className="px-4 py-3 text-right font-mono whitespace-nowrap" style={{ color: "var(--amber, #d97706)" }}>
                    −{fmtMXN(r.commission)}
                    {r.commission_pct != null && <span className="text-[9px] text-slate-500 ml-1">({r.commission_pct}%)</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold whitespace-nowrap" style={{ color: "var(--green)" }}>{fmtMXN(r.cash_received)}</td>
                  <td className="px-4 py-3">
                    {r.invoice_received ? (
                      <span data-testid={`cash-ex-invoiced-${r.id}`} title={r.invoice_uuid || ""}
                        onClick={() => { if (r.invoice_uuid) { navigator.clipboard?.writeText(r.invoice_uuid); toast.success("UUID copiado: " + r.invoice_uuid); } }}
                        className="cursor-pointer text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm whitespace-nowrap"
                        style={{ background: "#22c55e1f", color: "#22c55e", border: "1px solid #22c55e55" }}>✓ Facturado</span>
                    ) : (
                      <span data-testid={`cash-ex-noinvoice-${r.id}`} title="Pendiente de CFDI — aparece en Facturas Pendientes y en las sugerencias de Facturas SAT"
                        className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm whitespace-nowrap"
                        style={{ background: "#fbbf241f", color: "#fbbf24", border: "1px solid #fbbf2455" }}>Sin factura</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-300">{r.performed_by || r.created_by_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{r.reference || "—"}</td>
                </tr>
              ))}
            </tbody>
            {sums && rows.length > 0 && (
              <tfoot>
                <tr className="panel-header font-mono text-xs">
                  <td colSpan={3} className="px-4 py-2.5 label-mono">TOTALES (filtro)</td>
                  <td className="px-4 py-2.5 text-right font-bold text-slate-200">{fmtMXN(sums.transferido)}</td>
                  <td className="px-4 py-2.5 text-right font-bold" style={{ color: "var(--amber, #d97706)" }}>−{fmtMXN(sums.comisiones)}</td>
                  <td className="px-4 py-2.5 text-right font-bold" style={{ color: "var(--green)" }}>{fmtMXN(sums.recibido)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <Pager page={page} setPage={setPage} total={total} testId="cash-ex-pagination" />
      </div>
      {modal && <ExchangeModal editing={modal.editing} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); onChanged(); }} />}
      {viewer && <ReceiptViewer fileId={viewer} title="Comprobante de transferencia" onClose={() => setViewer(null)} />}
    </div>
  );
};

const DeliveriesTab = ({ canDelete, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [sums, setSums] = useState(null);
  const [page, setPage] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ delivery_date: todayLocal(), recipient_id: "", amount: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/cash/deliveries", { params: { limit: PAGE_SIZE, skip: page * PAGE_SIZE } });
    setRows(data.items || []); setTotal(data.total || 0); setSums(data.sums || null);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => { api.get("/cash/recipients").then(({ data }) => setRecipients(data || [])); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...form, amount: Math.round(Number(form.amount) * 100) / 100 };
      if (editing) await api.patch(`/cash/deliveries/${editing.id}`, payload);
      else await api.post("/cash/deliveries", payload);
      toast.success(editing ? "Entrega actualizada" : "Entrega registrada");
      setOpen(false); setEditing(null); load(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    setSaving(false);
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta entrega de efectivo?")) return;
    try { await api.delete(`/cash/deliveries/${id}`); load(); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <button data-testid="add-cash-delivery-btn" onClick={() => { setEditing(null); setForm({ delivery_date: todayLocal(), recipient_id: recipients[0]?.id || "", amount: "", notes: "" }); setOpen(true); }}
          className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
          <Plus className="w-4 h-4" /> Registrar entrega
        </button>
      </div>
      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="cash-deliveries-table">
            <thead className="panel-header">
              <tr className="text-left">
                {["Acciones", "Fecha", "Entregado a", "Monto entregado", "Notas", "Registró"].map((h) => (
                  <th key={h} className={`label-mono px-4 py-3 ${h === "Monto entregado" ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-500">Sin entregas registradas.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }} data-testid={`cash-del-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-0.5">
                      <button data-testid={`cash-del-edit-${r.id}`} onClick={() => { setEditing(r); setForm({ delivery_date: r.delivery_date, recipient_id: r.recipient_id, amount: r.amount, notes: r.notes || "" }); setOpen(true); }} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }} title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                      {canDelete && <button data-testid={`cash-del-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }} title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-300 whitespace-nowrap">{fmtDate(r.delivery_date)}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-slate-100">{r.recipient_name}</span>
                    {r.recipient_role && <span className="ml-2 text-[9px] uppercase tracking-widest text-slate-500">{r.recipient_role}</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold whitespace-nowrap" style={{ color: "var(--magenta, #d946ef)" }}>{fmtMXN(r.amount)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400 truncate max-w-[220px]" title={r.notes}>{r.notes || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{r.created_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
            {sums && rows.length > 0 && (
              <tfoot>
                <tr className="panel-header font-mono text-xs">
                  <td colSpan={3} className="px-4 py-2.5 label-mono">TOTAL ENTREGADO</td>
                  <td className="px-4 py-2.5 text-right font-bold" style={{ color: "var(--magenta, #d946ef)" }}>{fmtMXN(sums.entregado)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <Pager page={page} setPage={setPage} total={total} testId="cash-del-pagination" />
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(3,6,14,0.8)", backdropFilter: "blur(4px)" }}>
          <div className="panel w-full max-w-md p-5" data-testid="cash-delivery-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-slate-100">{editing ? "Editar entrega" : "Entregar efectivo a usuario"}</h3>
              <button onClick={() => setOpen(false)} className="p-1 rounded-sm hover:bg-white/5 text-slate-400"><XCircle className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <Sel label="Entregar a (usuario)" value={form.recipient_id} onChange={(v) => setForm({ ...form, recipient_id: v })}
                options={recipients.map((r) => ({ value: r.id, label: `${r.name} (${r.role})` }))} testId="cash-del-recipient" />
              {recipients.length === 0 && <div className="text-[10px] text-amber-500">No hay usuarios activos disponibles.</div>}
              <div className="grid grid-cols-2 gap-3">
                <In label="Fecha" type="date" value={form.delivery_date} onChange={(v) => setForm({ ...form, delivery_date: v })} testId="cash-del-date" />
                <In label="Monto MXN" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="cash-del-amount" />
              </div>
              <div>
                <label className="label-mono block mb-1.5">Notas</label>
                <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
                <button type="submit" data-testid="cash-del-save-btn" disabled={saving || !form.recipient_id} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">{saving ? "Guardando…" : editing ? "Actualizar" : "Guardar"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const ReconciliationTab = ({ summary }) => {
  const [expanded, setExpanded] = useState(null);
  const [expenses, setExpenses] = useState({});
  const residents = summary?.residents || [];

  const toggle = async (rid) => {
    if (expanded === rid) { setExpanded(null); return; }
    setExpanded(rid);
    if (!expenses[rid]) {
      try {
        const { data } = await api.get(`/cash/recipients/${rid}/expenses`);
        setExpenses((m) => ({ ...m, [rid]: data }));
      } catch { setExpenses((m) => ({ ...m, [rid]: [] })); }
    }
  };

  if (residents.length === 0) {
    return <div className="panel p-10 text-center text-slate-500 text-sm" data-testid="cash-recon-empty">Aún no hay entregas de efectivo. Registra una entrega para empezar a comprobar el recurso.</div>;
  }
  return (
    <div className="space-y-3" data-testid="cash-recon-list">
      <div className="text-[11px] text-slate-500">Se comprueban con gastos pagados en <b className="text-slate-300">EFECTIVO</b> (de obra y administrativos) con recurso de la <b className="text-slate-300">EMPRESA</b>: los que registra cada usuario y los que otros capturan y le <b className="text-slate-300">asignan como su comprobación</b>, desde la fecha de su primera entrega (los rechazados se regresan).</div>
      {residents.map((r) => {
        const pct = r.entregado > 0 ? Math.min(100, (r.comprobado / r.entregado) * 100) : 0;
        const list = expenses[r.recipient_id];
        return (
          <div key={r.recipient_id} className="panel overflow-hidden" data-testid={`cash-recon-${r.recipient_id}`}>
            <button onClick={() => toggle(r.recipient_id)} className="w-full px-4 py-3 flex items-center gap-4 text-left hover:bg-white/[0.03]">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-100 truncate">{r.name}</div>
                {r.first_delivery && <div className="text-[9px] font-mono text-slate-500 mt-0.5">Comprueba desde: {fmtDate(r.first_delivery)}</div>}
                <div className="h-1.5 rounded-full mt-2" style={{ background: "var(--border)" }}>
                  <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: r.saldo <= 0 ? "var(--green)" : "var(--cyan)" }} />
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] label-mono">ENTREGADO</div>
                <div className="font-mono text-sm" style={{ color: "var(--magenta, #d946ef)" }}>{fmtMXN(r.entregado)}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] label-mono">COMPROBADO</div>
                <div className="font-mono text-sm" style={{ color: "var(--green)" }}>{fmtMXN(r.comprobado)}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] label-mono">POR COMPROBAR</div>
                <div className="font-mono text-sm font-bold" style={{ color: r.saldo > 0 ? "var(--amber, #d97706)" : "var(--green)" }} data-testid={`cash-recon-saldo-${r.recipient_id}`}>{fmtMXN(r.saldo)}</div>
              </div>
              {expanded === r.recipient_id ? <ChevronUp className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />}
            </button>
            {expanded === r.recipient_id && (
              <div style={{ borderTop: "1px solid var(--border)" }}>
                {!list && <div className="px-4 py-4 text-xs text-slate-500">Cargando…</div>}
                {list && list.length === 0 && <div className="px-4 py-4 text-xs text-slate-500">Sin gastos en efectivo registrados por este residente.</div>}
                {list && list.length > 0 && (
                  <table className="w-full text-xs">
                    <thead><tr className="text-left panel-header">
                      {["Fecha", "Obra", "Descripción", "Registró", "Estado", "Monto"].map((h) => <th key={h} className={`label-mono px-4 py-2 ${h === "Monto" ? "text-right" : ""}`}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {list.map((e) => (
                        <tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
                          <td className="px-4 py-2 font-mono text-slate-400 whitespace-nowrap">{fmtDate(e.expense_date)}</td>
                          <td className="px-4 py-2 text-slate-300">{e.project_name}</td>
                          <td className="px-4 py-2 text-slate-200">{e.description}</td>
                          <td className="px-4 py-2" data-testid={`cash-recon-registered-by-${e.id}`}>
                            <span className="text-slate-300">{e.created_by_name || "—"}</span>
                            {e.cash_settlement_user_id ? (
                              <span className="block text-[9px] uppercase tracking-widest font-semibold" style={{ color: "var(--cyan)" }}>Asignado a {r.name}</span>
                            ) : (
                              <span className="block text-[9px] uppercase tracking-widest text-slate-500">Registro propio</span>
                            )}
                          </td>
                          <td className="px-4 py-2"><span className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: STATUS_COLORS[e.status] || "#94a3b8" }}>{e.status}</span></td>
                          <td className="px-4 py-2 text-right font-mono text-slate-100">{fmtMXN(e.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MyCashView = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/cash/my-summary").then(({ data }) => setS(data)).catch(() => {}); }, []);
  return (
    <div data-testid="my-cash-page">
      <PageHeader title="Mi Caja / Efectivo" subtitle="Efectivo recibido · Comprobación" testId="my-cash-header" />
      <div className="px-6 lg:px-8 py-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Kpi label="EFECTIVO RECIBIDO" value={s?.entregado} color="var(--green)" testId="mycash-kpi-recibido" big />
          <Kpi label="GASTADO EN EFECTIVO" value={s?.gastado} color="var(--cyan)" testId="mycash-kpi-gastado" big />
          <Kpi label="POR COMPROBAR" value={s?.saldo} color={(s?.saldo || 0) > 0 ? "var(--amber, #d97706)" : "var(--green)"} testId="mycash-kpi-saldo" big />
        </div>
        <div className="text-[11px] text-slate-500">Registra tus gastos pagados en <b className="text-slate-300">EFECTIVO</b> (de obra o administrativos) en la sección Gastos — se descuentan automáticamente de tu saldo (los rechazados se regresan).</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="panel overflow-hidden" data-testid="mycash-deliveries">
            <div className="panel-header px-4 py-3"><span className="label-mono">// ENTREGAS RECIBIDAS</span></div>
            <table className="w-full text-sm">
              <thead className="panel-header"><tr className="text-left">
                {["Fecha", "Monto", "Entregó", "Notas"].map((h) => <th key={h} className={`label-mono px-4 py-2.5 ${h === "Monto" ? "text-right" : ""}`}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(!s || s.deliveries.length === 0) && <tr><td colSpan={4} className="text-center py-8 text-slate-500 text-xs">Aún no has recibido entregas de efectivo.</td></tr>}
                {(s?.deliveries || []).map((d) => (
                  <tr key={d.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-300 whitespace-nowrap">{fmtDate(d.delivery_date)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold whitespace-nowrap" style={{ color: "var(--green)" }}>{fmtMXN(d.amount)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-300">{d.created_by_name || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-400 truncate max-w-[160px]" title={d.notes}>{d.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="panel overflow-hidden" data-testid="mycash-expenses">
            <div className="panel-header px-4 py-3"><span className="label-mono">// MIS GASTOS EN EFECTIVO</span></div>
            <table className="w-full text-sm">
              <thead className="panel-header"><tr className="text-left">
                {["Fecha", "Obra", "Descripción", "Estado", "Monto"].map((h) => <th key={h} className={`label-mono px-4 py-2.5 ${h === "Monto" ? "text-right" : ""}`}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(!s || s.expenses.length === 0) && <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-xs">Sin gastos en efectivo registrados.</td></tr>}
                {(s?.expenses || []).map((e) => (
                  <tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-400 whitespace-nowrap">{fmtDate(e.expense_date)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-300 truncate max-w-[120px]" title={e.project_name}>{e.project_name}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-200 truncate max-w-[160px]" title={e.description}>{e.description}</td>
                    <td className="px-4 py-2.5"><span className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: STATUS_COLORS[e.status] || "#94a3b8" }}>{e.status}</span></td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-100 whitespace-nowrap">{fmtMXN(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const TABS = [
  { id: "cambios", label: "Cambios", icon: Banknote },
  { id: "entregas", label: "Entregas", icon: HandCoins },
  { id: "comprobacion", label: "Comprobación", icon: ClipboardCheck },
  { id: "general", label: "Caja General", icon: Landmark, adminOnly: true },
];

export default function CashBox() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin";
  const [tab, setTab] = useState("cambios");
  const [summary, setSummary] = useState(null);
  const isField = ["residente", "supervisor", "contador"].includes(user?.role);

  const loadSummary = async () => {
    try { const { data } = await api.get("/cash/summary"); setSummary(data); } catch { /* noop */ }
  };
  useEffect(() => { if (!isField) loadSummary(); /* eslint-disable-next-line */ }, [isField]);

  if (isField) return <MyCashView />;

  return (
    <div data-testid="cashbox-page">
      <PageHeader title="Caja / Efectivo" subtitle="Cambios · Entregas · Comprobación" testId="cashbox-header" />
      <div className="px-6 lg:px-8 py-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Kpi label="SALDO EN CAJA" value={summary?.saldo_caja} color="var(--cyan)" testId="cash-kpi-saldo" big />
          <Kpi label="EFECTIVO RECIBIDO" value={summary?.recibido} color="var(--green)" testId="cash-kpi-recibido" />
          <Kpi label="COMISIONES" value={summary?.comisiones} color="var(--amber, #d97706)" testId="cash-kpi-comisiones" />
          <Kpi label="EFECTIVO ENTREGADO" value={summary?.entregado} color="var(--magenta, #d946ef)" testId="cash-kpi-entregado" />
          <Kpi label="POR COMPROBAR" value={summary?.por_comprobar} color="var(--red, #ef4444)" testId="cash-kpi-por-comprobar" />
        </div>
        {summary?.cutoff_date && (
          <div className="text-[11px] text-slate-500">
            El saldo descuenta entregas de efectivo y gastos en <b className="text-slate-300">EFECTIVO</b> que salen de caja general (los marcados "sale de caja" desde que se registran, y gastos de oficina pagados) desde el {fmtDate(summary.cutoff_date)} (primer cambio registrado). Gastos de caja en efectivo: <b className="text-slate-300">{fmtMXN(summary.gastos_oficina_efectivo)}</b>.
          </div>
        )}
        <div className="flex gap-1 flex-wrap" data-testid="cashbox-tabs">
          {TABS.filter((t) => !t.adminOnly || user?.role === "admin").map((t) => (
            <button key={t.id} data-testid={`cash-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-2 ${tab === t.id ? "btn-primary" : "btn-ghost"}`}
              style={tab !== t.id ? { border: "1px solid var(--border)" } : {}}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "cambios" && <ExchangesTab canDelete={canDelete} onChanged={loadSummary} />}
        {tab === "entregas" && <DeliveriesTab canDelete={canDelete} onChanged={loadSummary} />}
        {tab === "comprobacion" && <ReconciliationTab summary={summary} />}
        {tab === "general" && user?.role === "admin" && <TreasuryTab />}
      </div>
    </div>
  );
}
